import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Text from '@amzn/meridian/text'

import NavBar from '@components/NavBar/NavBar'
import StoreSwitcherHeader from '@components/StoreSwitcherHeader'
import SideBar from '@components/SideBar/SideBar'
import { InventoryViewerModal } from '@src/components/NavBar/InventoryViewerModal/InventoryViewerModal'
import { SiteAssistModal } from '@src/components/NavBar/SiteAssistModal/SiteAssistModal'
import { getDefaultMenuConfig, getRoleBasedMenuConfigByStoreFamily } from '@crossframe/apis/ToolMenuConfig'
import { getStoreInfo, isConfigured } from '@atredux/selectors/storeSelector'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getIsSeawardModalOpen } from '@atredux/selectors/iframeSelector'
import { getSeawardIFrame } from '@utils/ModalSwitchUtil'

import {
    getMenuConfig, getSubzoneToToolMap, getToolNameToDetailMap, getZoneToSubzoneMap, isAllowListedTool, OPERATIONS_TOOL_NAME,
} from '@services/ToolMenuConfigService'
import { getAllAllowedActions, isCurrentUserLoaded, isCurrentUserFailed, onCurrentUserLoaded } from '@api/userAndStoreContext'
import { RegisteredAssetsProvider } from '@amzn/jwo-store-management-platform-registration'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

const assetsProvider = new RegisteredAssetsProvider()

import './ToolLayout.scss'
import '../styles/buttons.scss'
import '../styles/utilities.scss'
import ChangeLanguageModal from './SideBar/ChangeLanguageModal'
import StoreScanRequiredModal from '@components/StoreScanRequiredModal'
import StoreSelectionRequiredModal from '@components/StoreSelectionRequiredModal'
import InterruptModal from '@components/InterruptModal'
import AtAlertModal from '@components/AtAlertModal'
import MIModal from '@components/MIModal'
import IdlePrompt from '@components/IdlePrompt'
import NotificiationModalController from '@components/NotificationModalController'

import { usePageLatencyTracking } from '@src/hooks/usePageLatencyTracking'
import { usePlatformMode } from '@hooks/usePlatformMode'
import { useSoftKeyboardOpen } from '@hooks/useSoftKeyboardOpen'
import { bottomNavConfig } from '@utils/ui-config'
import AnnouncementsErrorBoundary from '@src/components/Announcements/AnnouncementsErrorBoundary'
import AnnouncementsModalController from '@src/components/Announcements/AnnouncementsModalController'
import { AtConnectionsModal } from '@components/ConnectionsModal'
import { getInventoryViewerModalState } from '@atredux/selectors/inventoryViewerModalSelector'
import { getSiteAssistModalState } from '@src/redux/selectors/siteAssistSelector'
import { FrequentlyUsedToolsConstants } from '@src/utils/Constants/FrequentlyUsedToolsConstants'
import { getNotificationModalState, getNotificationSheetState } from '@atredux/selectors/notificationSelector'
import { NotificationModal } from '@src/components/NavBar/NotificationModal/NotificationModal'
import { ExitSheet } from '@components/Sheet/ExitSheet'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { MenuSheet } from '@components/Sheet/MenuSheet'
import { MenuSheetOption } from '@components/Sheet/MenuSheetOptions'
import { DebugStoreScanButton } from './DebugStoreScanButton'
/**
 * Returns true when the scanIntoStore URL parameter is set to 'true'.
 * Used to gate the DebugStoreScanButton for developer-only access.
 */
import { isScanIntoStoreEnabled, isBottomNavEnabled } from '@utils/EnvironmentUtil'
import { useFeatureFlag } from '@services/featureFlags'
import { NotificationsSheet } from '@src/pages/NotificationsPage/NotificationsSheet'
import { ProfileSettingsSheet } from '@src/pages/ProfileSettingsPage/ProfileSettingsSheet'
import { NOTIFICATIONS_TOOL_NAME } from '@src/pages/NotificationsPage/constants'
import { PROFILE_TOOL_NAME } from '@src/pages/ProfileSettingsPage/constants/profileSettings'
import { setNotificationSheetOpen } from '@atredux/slices/notificationSlice'
import HomePage from '@pages/HomePage'
import OperationsPage from '@pages/OperationsPage'
import BottomNavBar from '@components/BottomNav/BottomNavBar'
import StoreToolsPage from '@components/BottomNav/StoreToolsPage'

/**
 * Root component that renders all the critical subcomponents
 * for the entire application. It also holds UI specific state required
 * by multiple subcomponents.
 *
 * @returns {React.ReactElement}
 */
const ToolLayout = (props: any): React.ReactElement => {

    const platformMode = usePlatformMode()
    // Handhelds on an opted-in domain only; everything else keeps the side nav.
    const isBottomNavMode = platformMode !== 'cloudscape' && isBottomNavEnabled()
    // The Android shell resizes the WebView for the keyboard, which docks the fixed nav on top of
    // it and over the focused field, so the nav steps aside while the keyboard is up.
    const isSoftKeyboardOpen = useSoftKeyboardOpen()
    const showBottomNav = isBottomNavMode && !isSoftKeyboardOpen
    const [bundle] = useBundle(BUNDLE_NAME)
    const dispatch = useDispatch()
    const selectedTool = useSelector(getSelectedTool)

    // Keep the Operations page mounted after its first visit so its Store Health View iframe is
    // not destroyed (and reloaded) when the user navigates to another nav item and back. Visibility
    // is toggled via a `hidden` prop (display:none) rather than unmounting, which preserves the iframe.
    const isOperationsActive = platformMode === 'cloudscape' && selectedTool.displayName === OPERATIONS_TOOL_NAME
    // Initialize from the computed value so a deep-link / refresh straight onto Operations renders it
    // on the first frame (no blank flash); the effect below keeps it mounted after later visits.
    const [operationsVisited, setOperationsVisited] = useState(isOperationsActive)
    useLayoutEffect(() => {
        if (isOperationsActive) {
            setOperationsVisited(true)
        }
    }, [isOperationsActive])
    const [showMenuButton, setShowMenuButton] = useState(true)
    const [isUserLoaded, setIsUserLoaded] = useState(isCurrentUserLoaded())
    const allowedActions = getAllAllowedActions()
    const registeredAssets = useMemo(() => assetsProvider.getRegisteredAssets(), [])

    const storeInfo = useSelector(getStoreInfo)
    const { enabled: shvMobileEnabled } = useFeatureFlag('shv_mobile_v1')
    useEffect(() => {
        if (!isUserLoaded) {
            onCurrentUserLoaded(() => setIsUserLoaded(true))
        }
    }, [isUserLoaded])

    // Filter tools by checking if user's allowedActions overlap with tool's supportedActions
    const filteredSubzoneToToolMap = useMemo(() => {
        if (!isUserLoaded || isCurrentUserFailed()) return getSubzoneToToolMap()
        // Store Health View on mobile is gated by flag
        const shvHidden = platformMode === 'meridian' && !shvMobileEnabled
        const isShvTool = (asset?: { assetId?: string }) => asset?.assetId === 'jwo-store-health-app'
        const hidden: string[] = []
        const filtered = new Map<string, string[]>()
        getSubzoneToToolMap().forEach((tools, key) => {
            const visible = tools.filter((toolDisplayName) => {
                const asset = registeredAssets.find((a) => a.displayName === toolDisplayName)
                if (shvHidden && isShvTool(asset)) {
                    hidden.push(toolDisplayName)
                    return false
                }
                // Tag-based visibility: check store tags match (P400047153)
                if (storeInfo && asset?.availability?.storeFamilies?.length) {
                    if (!isAllowListedTool(asset)) {
                        hidden.push(toolDisplayName)
                        return false
                    }
                }
                if (!asset?.supportedActions?.length) return true
                const hasAccess = asset.supportedActions.some((a: string) => allowedActions.includes(a))
                if (!hasAccess) hidden.push(toolDisplayName)
                return hasAccess
            })
            if (visible.length > 0) filtered.set(key, visible)
        })
        if (hidden.length > 0) {
            console.info('[ToolLayout] Hidden tools (insufficient actions):', hidden)
        }
        return filtered
    // storeInfo: re-evaluate tool visibility and display names on every store switch
    }, [allowedActions, isUserLoaded, registeredAssets, storeInfo, platformMode, shvMobileEnabled])

    // Compute display name overrides based on store tags (P401362771)
    // Kept separate from the tool map so click handlers use the original registered name
    const displayNameOverrides = useMemo(() => {
        const overrides = new Map<string, string>()
        if (!storeInfo?.tags) return overrides
        registeredAssets.forEach((asset) => {
            const override = asset?.availability?.displayNameOverride
            if (!override) return
            const tagsPresent = override.tags.every((tag: string) => storeInfo.tags.includes(tag))
            const shouldRename = override.present ? tagsPresent : !tagsPresent
            if (shouldRename) {
                overrides.set(asset.displayName, override.displayName)
            }
        })
        return overrides
    }, [registeredAssets, storeInfo])

    const [isMenuOpen, setMenuOpen] = useState(() => {
        // In cloudscape mode, sidebar is always visible
        if (platformMode === 'cloudscape') {
            return true
        }
        // Default behavior - keep closed on load so Store Selection Sheet appears
        console.log('Setting Menu state to false')
        return false
    })

    const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

    const navBarRef = useRef()

    // Control bottom sheet when profile icon button is clicked on NavBar
    const [isProfileOpen, setProfileOpen] = useState(false)
    const { enabled: profileEnabled } = useFeatureFlag('profile_settings_v1')
    const toggleProfile = useCallback(() => {
        if (!isProfileOpen) {
            dispatch(setNotificationSheetOpen(false))
        }
        setProfileOpen(!isProfileOpen)
    }, [isProfileOpen, dispatch])

    // Control bottom sheet when help icon button is clicked on NavBar
    const [isProblemSolveOpen, setProblemSolveOpen] = useState(false)
    const toggleProblemSolve = useCallback(() => setProblemSolveOpen(!isProblemSolveOpen), [isProblemSolveOpen])

    // Control notifications sheet - use Redux state
    const isNotificationsSheetOpen = useSelector(getNotificationSheetState)
    const toggleNotificationsSheet = useCallback(() => {
        if (isMenuOpen) {
            setMenuOpen(false)
            // If notifications are already open behind the sidebar, just close the sidebar
            if (isNotificationsSheetOpen) return
        }
        if (!isNotificationsSheetOpen) {
            setProfileOpen(false)
        }
        dispatch(setNotificationSheetOpen(!isNotificationsSheetOpen))
    }, [dispatch, isNotificationsSheetOpen, isMenuOpen])

    /* Sheets otherwise survive a bottom-nav tab change and cover the tab the user just picked,
       so the nav looks broken. The masthead toggles above already close each other this way. */
    const dismissOverlaySheets = useCallback(() => {
        setProfileOpen(false)
        dispatch(setNotificationSheetOpen(false))
    }, [dispatch])

    /* The state flag - showNewHomePageFeatures  is used to enable new features like profile icon button, help icon button,
       toggling between menu button and new close button when inside a tool for the new home page. It is enabled when the weblab return
       true and current tool is allowlisted. Will be removed after it is launched in all stores
     */
    const [showNewHomePageFeatures, setShowNewHomePageFeatures] = useState(false)
    useEffect(() => {
        const checkHomePageFeatures = async () => {
            try {
                // Disable Tasks home page feature
                const isEnabled = false
                /* Don't remove space_dock tool (display name - Tasks) since that is the new home page. Add new tool_names
                   as they are enabled from new home page
                   test_tool has been just for unit tests
                */
                const allowlistToolNames = ['space_dock','test_tool']
                setShowNewHomePageFeatures(isEnabled && allowlistToolNames.includes(selectedTool.toolName))
                const allowlistToolNamesforCloseButton = ['test_tool']
                setShowMenuButton(!isEnabled || !allowlistToolNamesforCloseButton.includes(selectedTool.toolName))
            } catch (error) {
                console.warn('Failed to check TMOE_HOME_PAGE_1285398 weblab:', error)
                setShowNewHomePageFeatures(false)
            }
        }

        checkHomePageFeatures()
    }, [selectedTool])

    // Toggle between hamburger menu button when user is in home page or close button when user is in a tool
    const toggleMenuButton = useCallback(() => {
        // uncomment this once above allowlistToolNamesforCloseButton logic is not needed
        // setShowMenuButton(!showMenuButton)
    }, [showMenuButton, showNewHomePageFeatures])

    // Control bottom sheet when close icon button is clicked on NavBar when user in a tool
    const [isExitSheetOpen, setExitSheetOpen] = useState(false)

    const [changeLanguageOpen, setChangeLanguageOpen] = useState(false)
    const [isFrequentlyUsedToolsEnabled, setIsFrequentlyUsedToolsEnabled] = useState<boolean>(false)
    const isIVModalOpen = useSelector(getInventoryViewerModalState)
    const isSiteAssistModalOpen = useSelector(getSiteAssistModalState)
    const isNotificationModalOpen = useSelector(getNotificationModalState)

    const configured = useSelector(isConfigured)
    const seawardModalOpen = useSelector(getIsSeawardModalOpen)
    const navigate = useNavigate()

    usePageLatencyTracking(props)

    /**
     * Checks weblab to see if the frequently used tools feature is enabled.
     * Sets the isFrequentlyUsedToolsEnabled state variable to true if the weblab is enabled.
     */
    const checkIfFrequentlyUsedToolsLayoutIsEnabled = () => {
        if (FrequentlyUsedToolsConstants.isFrequentlyUsedToolsEnabledForStoreFamily(storeInfo?.storeFamily)) {
            // Hardcoded: WeblabService removed during CrossFrameUtils migration
            const isWeblabEnabled = false
            console.log(`JWOStoreManagementPlatform: Is frequently used tools feature enabled? ${isWeblabEnabled}`)
            setIsFrequentlyUsedToolsEnabled(isWeblabEnabled)
        }
    }

    useEffect(() => {
        console.log('JWOStoreManagementPlatform: Configured: ', configured)
        if (!configured) {
            console.log('JWOStoreManagementPlatform: App not configured, redirecting to provisioning...')
            navigate('/provision')
        }
    }, [])

    useEffect(() => {
        // Check if the frequently used tools layout is enabled whenever the menu is opened.
        if (isMenuOpen) {
            checkIfFrequentlyUsedToolsLayoutIsEnabled()
        }
    }, [isMenuOpen])

    // userEmail is set by the Android RN shell before the app loads and does not
    // change while the platform is open, so a one-time read is sufficient.
    const hasUserEmail = Boolean(localStorage.getItem('userEmail'))

    useLayoutEffect(() => {
        document.body.style.setProperty(
            '--email-header-offset',
            hasUserEmail ? '25px' : '0px',
        )
        return () => { document.body.style.removeProperty('--email-header-offset') }
    }, [hasUserEmail])

    // The nav is fixed, so the page must give up that height or the nav covers the
    // bottom of the tool iframe.
    useLayoutEffect(() => {
        if (!isBottomNavMode) {
            return undefined
        }
        // While the keyboard hides the nav, its reserved height goes back to the page, so the
        // space between the focused field and the keyboard is the page's to use.
        document.body.style.setProperty('--bottom-nav-height', showBottomNav ? bottomNavConfig.BOTTOM_NAV_HEIGHT : '0px')
        document.body.classList.add('has-bottom-nav')
        return () => {
            document.body.classList.remove('has-bottom-nav')
            document.body.style.removeProperty('--bottom-nav-height')
        }
    }, [isBottomNavMode, showBottomNav])

    // NavBar's ToolName renders display text, so the override is resolved to a
    // string here. displayNameOverrides values come from the asset registry rather
    // than Panther, so they pass through untranslated.
    const overrideToolName = isMenuOpen
        ? t(bundle, 'jsma-web-common-storeTools')
        : isNotificationsSheetOpen
            ? t(bundle, NOTIFICATIONS_TOOL_NAME)
            : isProfileOpen
                ? t(bundle, PROFILE_TOOL_NAME)
                : displayNameOverrides.get(selectedTool?.displayName) || undefined

    // @ts-ignore
    return (
        // Asana: https://app.asana.com/1/8442528107068/project/1213997537694234/task/1215183552021117?focus=true
        <div
            style={{
                width: '100%',
                ...(platformMode === 'cloudscape' && selectedTool.toolName !== undefined
                    && selectedTool.displayName !== 'Welcome' ? { height: 0, overflow: 'hidden' } : {}),
            }}
            className={`platform-mode-${platformMode}`}
        >
            <NavBar
                toggleMenu={toggleMenu}
                showNewHomePageFeatures={showNewHomePageFeatures}
                showMenuButton={showMenuButton}
                openExitSheet={() => setExitSheetOpen(true)}
                toggleProfile={toggleProfile}
                toggleProblemSolve={toggleProblemSolve}
                toggleNotificationsSheet={toggleNotificationsSheet}
                overrideToolName={overrideToolName}
                isMenuOpen={isMenuOpen}
                isProfileOpen={isProfileOpen}
                isBottomNavMode={isBottomNavMode}
                ref={navBarRef}
            />
            <div className={`store-switcher-header-container ${isNotificationsSheetOpen ? 'notifications-open' : ''} ${isProfileOpen ? 'profile-open' : ''} ${isBottomNavMode && isMenuOpen ? 'tools-open' : ''}`}>
                {/* On store switch, close the menu so StoreToolsPage (open={isMenuOpen}) returns null
                    and unmounts, revealing the Associate Entry screen underneath that the switch
                    already navigated to. Guarded to bottomNavMode. The notifications and profile
                    sheets are closed too: they render above Associate Entry, so a switch made from
                    either would otherwise look like it did nothing. */}
                <StoreSwitcherHeader onStoreSwitched={() => {
                    if (isBottomNavMode) setMenuOpen(false)
                    dismissOverlaySheets()
                }} />
            </div>
            <NotificationsSheet
                open={isNotificationsSheetOpen}
                onClose={() => dispatch(setNotificationSheetOpen(false))}
            />
            {profileEnabled && (
                <ProfileSettingsSheet
                    open={isProfileOpen}
                    onClose={() => setProfileOpen(false)}
                />
            )}
            {/* Stays mounted even in bottom-nav mode because it bootstraps the initial
                zone/tool from the native shell; its sheet is just held closed. */}
            <SideBar
                subzoneToToolMap={filteredSubzoneToToolMap}
                displayNameOverrides={displayNameOverrides}
                zoneToSubzoneMap={getZoneToSubzoneMap()}
                isMenuOpen={isBottomNavMode ? false : isMenuOpen}
                toggleMenu={toggleMenu}
                toggleMenuButton={toggleMenuButton}
                setChangeLanguageOpen={setChangeLanguageOpen}
                isFrequentlyUsedToolsEnabled={isFrequentlyUsedToolsEnabled}
                platformMode={platformMode}
            />
            <ChangeLanguageModal open={changeLanguageOpen} setOpen={setChangeLanguageOpen} />
            {isBottomNavMode &&
                <>
                    <StoreToolsPage
                        open={isMenuOpen}
                        subzoneToToolMap={filteredSubzoneToToolMap}
                        displayNameOverrides={displayNameOverrides}
                        toggleMenu={toggleMenu}
                        toggleMenuButton={toggleMenuButton}
                    />
                    {showBottomNav &&
                        <BottomNavBar
                            isToolsPageOpen={isMenuOpen}
                            toggleMenu={toggleMenu}
                            toggleMenuButton={toggleMenuButton}
                            dismissOverlaySheets={dismissOverlaySheets}
                        />
                    }
                </>
            }
            {platformMode === 'cloudscape' && selectedTool.displayName === 'Welcome' &&
                <HomePage />
            }
            {platformMode === 'cloudscape' && operationsVisited &&
                <OperationsPage hidden={!isOperationsActive} />
            }
            {platformMode !== 'cloudscape' && selectedTool.displayName === 'Welcome' && seawardModalOpen === false && getSeawardIFrame()?.item(0)?.style?.display === 'none' &&
                <Text type="h600" alignment="center" data-atb-id="welcome-page-text"> Welcome </Text>
            }
            <InventoryViewerModal isIVModalOpen={isIVModalOpen}/>
            <NotificationModal isNotificationModalOpen={isNotificationModalOpen}/>
            <SiteAssistModal isSiteAssistModalOpen={isSiteAssistModalOpen}/>
            <StoreScanRequiredModal />
            <StoreSelectionRequiredModal />
            <InterruptModal />
            <AtAlertModal />
            <MIModal />
            <IdlePrompt />
            <AtConnectionsModal />
            <AnnouncementsErrorBoundary>
                <AnnouncementsModalController />
            </AnnouncementsErrorBoundary>
            <NotificiationModalController/>
            <ExitSheet id={'exit-sheet'} open={isExitSheetOpen}
                       setSheetOpen={setExitSheetOpen}
                       onClickPrimary={() => {
                           setExitSheetOpen(false)
                           _navigateToTool(getToolNameToDetailMap().get('Tasks'), toggleMenu, toggleMenuButton)
                       }}
                       enableCloseButton={true}/>
            <MenuSheet id={'problem-solve-sheet'} open={isProblemSolveOpen} setSheetOpen={setProblemSolveOpen}>
                <MenuSheetOption onClick={() => {
                    toggleProblemSolve()
                    setExitSheetOpen(true)
                }}
                >
                    <Text type="b400">
                        Exit and return to the task list
                    </Text>
                </MenuSheetOption>
            </MenuSheet>
            {/* Show debug scan button only for Associate Entry Tool when scanIntoStore URL param is true */}
            {selectedTool.displayName === 'Associate Entry' && isScanIntoStoreEnabled() && <DebugStoreScanButton />}
        </div >
    )
}

export default ToolLayout
